import TableRowsIcon from "@mui/icons-material/TableRows";
import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDepartmentActionsMetadata } from "../../components/RequestData/hooks/useAgentHistory";
import { FillShiftModal } from "../../components/RequestData/NotificationsHistory/FillShiftModal";
import { type FlatSwitchOption, SystemFlatSwitch } from "../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { DateRangeSelector } from "../../components/UIToolkit/molecules/DateRangeSelector";
import { EmptyState } from "../../components/UIToolkit/molecules/EmptyState";
import { SystemDataGrid, type SystemGridColDef } from "../../components/UIToolkit/Tables/SystemDataGrid";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { shiftPickupReportGroupedSortingKey, shiftPickupReportRawSortingKey } from "../../services/LocalStorageService";
import { formatTimeBefore } from "../../utils/dateFormatting";
import type { NurseFullRole } from "../../utils/models";
import { AgentReportView, AgentStaffAction } from "../../utils/models";
import type { MultiSortOption, SortingState } from "../../utils/multiColumnSorting";
import { getRoleGroups } from "../../utils/roles";
import { GroupedStatusCell, getInteractionStatusLabel, RawRoleGroupCell } from "./ShiftPickupRawCells";
import { getConfirmationStatusCell, getShiftFinalStatusKind, getShiftFinalStatusLabel, type ShiftFinalStatusKind } from "./shiftPickupReportStatus";

/** Row model for the grouped (per-shift) SystemDataGrid — typed values so the grid sorts correctly. */
type GroupedGridRow = {
	requestId: string;
	date: DateTime;
	hours: string;
	roleGroup: string[];
	messageAmount: number;
	callsAmount: number;
	completedCallsAmount: number;
	/** Localized status label shown in the cell. */
	status: string;
	/** Language-independent status classification — drives the icon (so translating `status` can't break it). */
	statusKind: ShiftFinalStatusKind;
	acceptedAmount: number;
	neededNurses: number;
	timeBeforeShift: string;
	shift_start: DateTime;
	shift_end: DateTime;
};

/** One flat row in the raw-data SystemDataGrid — pre-formatted display strings for search/sort. */
type RawGridRow = {
	id: string;
	shiftDate: string;
	shiftHours: string;
	roleGroups: string[];
	timeBeforeShift: string;
	staffName: string;
	interactionDate: string;
	interactionHour: string;
	interactionType: string;
	interactionStatus: string;
	confirmationStatus: string;
	confirmationTime: string;
};

const ShiftPickupReport = () => {
	const { t } = useTranslation();
	const { chosenDepartment, getNurseFormattedName } = useHospitalData();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const currentDate = useMemo(() => DateTime.now(), []);
	const [startDate, setStartDate] = useState<DateTime>(currentDate.minus({ months: 1 }));
	const [endDate, setEndDate] = useState<DateTime>(currentDate);
	const [viewMode, setViewMode] = useState<AgentReportView>(AgentReportView.Grouped);
	const [selectedRequest, setSelectedRequest] = useState<{ requestId: string; shift_start: DateTime; shift_end: DateTime } | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const isRawView = viewMode === AgentReportView.Raw;

	const { data: reportData, isLoading } = useDepartmentActionsMetadata({ startTime: startDate, endTime: endDate });
	const {
		data: rawReportData,
		isLoading: isRawLoading,
		isError: isRawError,
	} = useDepartmentActionsMetadata({ startTime: startDate, endTime: endDate, view: AgentReportView.Raw, enabled: isRawView });

	const viewModeOptions = useMemo<FlatSwitchOption<AgentReportView>[]>(
		() => [
			{ label: t("shift_pickup_report.view.grouped"), value: AgentReportView.Grouped, "data-testid": "shift-pickup-view-grouped" },
			{ label: t("shift_pickup_report.view.raw"), value: AgentReportView.Raw, "data-testid": "shift-pickup-view-raw" },
		],
		[t],
	);

	const groupedSortingOptions = useMemo<MultiSortOption<GroupedGridRow>[]>(
		() => [
			{ label: t("shift_pickup_report.sort.date"), sortColumns: ["date"] },
			{ label: t("shift_pickup_report.sort.hours"), sortColumns: ["hours"] },
			{ label: t("shift_pickup_report.sort.role_group"), sortColumns: ["roleGroup"] },
			{ label: t("shift_pickup_report.sort.message_amount"), sortColumns: ["messageAmount"] },
			{ label: t("shift_pickup_report.sort.calls_amount"), sortColumns: ["callsAmount"] },
			{ label: t("shift_pickup_report.sort.status"), sortColumns: ["status"] },
			{ label: t("shift_pickup_report.sort.accepted_amount"), sortColumns: ["acceptedAmount"] },
			{ label: t("shift_pickup_report.sort.needed_nurses"), sortColumns: ["neededNurses"] },
			{ label: t("shift_pickup_report.sort.time_before_shift"), sortColumns: ["timeBeforeShift"] },
		],
		[t],
	);

	const groupedDefaultSortState = useMemo<SortingState<GroupedGridRow>[]>(
		() => [{ sortColumns: ["date"], sortOrder: "desc", label: t("shift_pickup_report.sort.date") }],
		[t],
	);

	const rowData = useMemo<GroupedGridRow[]>(() => {
		if (!reportData?.requests) return [];

		const filteredData: GroupedGridRow[] = [];

		for (const requestMetadata of reportData.requests) {
			const requestDate = DateTime.fromISO(requestMetadata.shift_start);
			if (requestDate < startDate || requestDate > endDate.plus({ days: 1 })) {
				continue;
			}

			const jobName = jobManagementEnabled && requestMetadata.job_id ? chosenDepartment.jobs?.find((j) => j.id === requestMetadata.job_id)?.displayName : null;
			const roleGroups = jobName ? [jobName] : getRoleGroups((requestMetadata.roles || []) as NurseFullRole[], chosenDepartment);

			const shiftStart = DateTime.fromISO(requestMetadata.shift_start);
			const shiftEnd = DateTime.fromISO(requestMetadata.shift_end);
			const hours = `${shiftStart.toFormat("h:mm a")} - ${shiftEnd.toFormat("h:mm a")}`;

			const acceptedAmount = requestMetadata.approved_count;
			const neededNurses = requestMetadata.needed_nurses;

			// Final status shared with the raw view: a language-independent kind (drives the icon) plus a
			// localized label (the cell text), both derived from the same source so they can't desync.
			const statusKind = getShiftFinalStatusKind(requestMetadata.status, acceptedAmount, neededNurses);
			const status = getShiftFinalStatusLabel(t, requestMetadata.status, acceptedAmount, neededNurses);

			// Calculate time before shift - find earliest agent action created_at
			const earliestAction = requestMetadata.actions.reduce(
				(earliest, action) => {
					const actionTime = DateTime.fromISO(action.created_at);
					return !earliest || actionTime < earliest ? actionTime : earliest;
				},
				null as DateTime | null,
			);

			filteredData.push({
				requestId: requestMetadata.request_id,
				date: requestDate,
				hours,
				roleGroup: roleGroups,
				messageAmount: requestMetadata.message_amount,
				callsAmount: requestMetadata.calls_amount,
				completedCallsAmount: requestMetadata.completed_calls_amount,
				status,
				statusKind,
				acceptedAmount,
				neededNurses,
				timeBeforeShift: formatTimeBefore(requestDate, earliestAction),
				shift_start: shiftStart,
				shift_end: shiftEnd,
			});
		}

		return filteredData;
	}, [reportData, startDate, endDate, chosenDepartment, jobManagementEnabled, t]);

	const groupedColumns = useMemo<SystemGridColDef<GroupedGridRow>[]>(
		() => [
			{
				field: "date",
				headerName: t("shift_pickup_report.column.date"),
				flex: 1,
				minWidth: 120,
				renderCell: ({ row }) => row.date.toFormat("MMM dd, yyyy"),
				getExportValue: (row) => row.date.toFormat("MMM dd, yyyy"),
			},
			{ field: "hours", headerName: t("shift_pickup_report.column.hours"), flex: 1.1, minWidth: 150, align: "center", headerAlign: "center" },
			{
				field: "roleGroup",
				headerName: jobManagementEnabled ? t("shift_pickup_report.column.job") : t("shift_pickup_report.column.role_group"),
				flex: 1,
				minWidth: 120,
				align: "center",
				headerAlign: "center",
				renderCell: ({ row }) => <RawRoleGroupCell roleGroups={row.roleGroup} />,
				getExportValue: (row) => row.roleGroup.join(", "),
			},
			{ field: "messageAmount", headerName: t("shift_pickup_report.column.messages"), flex: 0.6, minWidth: 90, align: "center", headerAlign: "center" },
			{
				field: "callsAmount",
				headerName: t("shift_pickup_report.column.calls"),
				flex: 0.6,
				minWidth: 90,
				align: "center",
				headerAlign: "center",
				renderCell: ({ row }) => `${row.completedCallsAmount} / ${row.callsAmount}`,
				getExportValue: (row) => `${row.completedCallsAmount} / ${row.callsAmount}`,
			},
			{
				field: "status",
				headerName: t("shift_pickup_report.column.status"),
				flex: 1.4,
				minWidth: 160,
				align: "center",
				headerAlign: "center",
				renderCell: ({ row }) => <GroupedStatusCell status={row.status} statusKind={row.statusKind} />,
			},
			{ field: "acceptedAmount", headerName: t("shift_pickup_report.column.accepted"), flex: 0.6, minWidth: 90, align: "center", headerAlign: "center" },
			{ field: "neededNurses", headerName: t("shift_pickup_report.column.needed"), flex: 0.6, minWidth: 90, align: "center", headerAlign: "center" },
			{
				field: "timeBeforeShift",
				headerName: t("shift_pickup_report.column.time_before_shift"),
				flex: 1.2,
				minWidth: 140,
				align: "center",
				headerAlign: "center",
			},
		],
		[t, jobManagementEnabled],
	);

	const rawRows = useMemo<RawGridRow[]>(() => {
		if (!rawReportData?.raw_interactions) return [];

		return rawReportData.raw_interactions.map((interaction, index) => {
			const shiftStart = DateTime.fromISO(interaction.shift_start);
			const shiftEnd = DateTime.fromISO(interaction.shift_end);
			const actionTime = interaction.action_time ? DateTime.fromISO(interaction.action_time) : null;
			const acceptedAt = interaction.accepted_at ? DateTime.fromISO(interaction.accepted_at) : null;
			const agentStartedAt = interaction.agent_started_at ? DateTime.fromISO(interaction.agent_started_at) : null;

			const jobName = jobManagementEnabled && interaction.job_id ? chosenDepartment.jobs?.find((j) => j.id === interaction.job_id)?.displayName : null;
			const roleGroups = jobName ? [jobName] : getRoleGroups((interaction.roles || []) as NurseFullRole[], chosenDepartment);

			return {
				// No per-interaction id exists in the payload; the array order is stable & deduped server-side.
				id: `${interaction.request_id}-${interaction.nurse_id}-${interaction.interaction_type}-${index}`,
				shiftDate: shiftStart.toFormat("MMM dd, yyyy"),
				shiftHours: `${shiftStart.toFormat("h:mm a")} - ${shiftEnd.toFormat("h:mm a")}`,
				roleGroups,
				timeBeforeShift: formatTimeBefore(shiftStart, agentStartedAt),
				staffName: getNurseFormattedName(interaction.nurse_id),
				interactionDate: actionTime ? actionTime.toFormat("MMM dd, yyyy") : "",
				interactionHour: actionTime ? actionTime.toFormat("h:mm a") : "",
				interactionType:
					interaction.interaction_type === AgentStaffAction.Call
						? t("shift_pickup_report.interaction_type.call")
						: t("shift_pickup_report.interaction_type.text"),
				interactionStatus: getInteractionStatusLabel(t, interaction.interaction_type, interaction.response_status, interaction.interaction_status),
				confirmationStatus: getConfirmationStatusCell(t, interaction),
				confirmationTime: acceptedAt ? acceptedAt.toFormat("MMM dd, yyyy h:mm a") : "",
			};
		});
	}, [rawReportData, chosenDepartment, jobManagementEnabled, getNurseFormattedName, t]);

	const rawColumns = useMemo<SystemGridColDef<RawGridRow>[]>(
		() => [
			{ field: "shiftDate", headerName: t("shift_pickup_report.raw.column.shift_date"), flex: 1, minWidth: 120 },
			{ field: "shiftHours", headerName: t("shift_pickup_report.raw.column.shift_hours"), flex: 1.1, minWidth: 150 },
			{
				field: "roleGroups",
				headerName: jobManagementEnabled ? t("shift_pickup_report.raw.column.job") : t("shift_pickup_report.raw.column.role_group"),
				flex: 0.9,
				minWidth: 120,
				sortable: false,
				renderCell: ({ row }) => <RawRoleGroupCell roleGroups={row.roleGroups} />,
				// renderCell-only column: serialize the pills to a plain string for XLSX/CSV export.
				getExportValue: (row) => row.roleGroups.join(", "),
			},
			{ field: "timeBeforeShift", headerName: t("shift_pickup_report.raw.column.time_before_shift"), flex: 1, minWidth: 130 },
			{ field: "staffName", headerName: t("shift_pickup_report.raw.column.staff_name"), flex: 1.2, minWidth: 150 },
			{ field: "interactionDate", headerName: t("shift_pickup_report.raw.column.interaction_date"), flex: 1, minWidth: 120 },
			{ field: "interactionHour", headerName: t("shift_pickup_report.raw.column.interaction_hour"), flex: 0.8, minWidth: 110 },
			{ field: "interactionType", headerName: t("shift_pickup_report.raw.column.interaction_type"), flex: 0.7, minWidth: 100 },
			{ field: "interactionStatus", headerName: t("shift_pickup_report.raw.column.interaction_status"), flex: 1, minWidth: 130 },
			{ field: "confirmationStatus", headerName: t("shift_pickup_report.raw.column.confirmation_status"), flex: 1.2, minWidth: 160 },
			{ field: "confirmationTime", headerName: t("shift_pickup_report.raw.column.confirmation_time"), flex: 1.2, minWidth: 160 },
		],
		[t, jobManagementEnabled],
	);

	// Raw sort labels reuse the raw column headers (identical text).
	const rawSortingOptions = useMemo<MultiSortOption<RawGridRow>[]>(
		() => [
			{ label: t("shift_pickup_report.raw.column.shift_date"), sortColumns: ["shiftDate"] },
			{ label: t("shift_pickup_report.raw.column.shift_hours"), sortColumns: ["shiftHours"] },
			{ label: t("shift_pickup_report.raw.column.time_before_shift"), sortColumns: ["timeBeforeShift"] },
			{ label: t("shift_pickup_report.raw.column.staff_name"), sortColumns: ["staffName"] },
			{ label: t("shift_pickup_report.raw.column.interaction_date"), sortColumns: ["interactionDate"] },
			{ label: t("shift_pickup_report.raw.column.interaction_hour"), sortColumns: ["interactionHour"] },
			{ label: t("shift_pickup_report.raw.column.interaction_type"), sortColumns: ["interactionType"] },
			{ label: t("shift_pickup_report.raw.column.interaction_status"), sortColumns: ["interactionStatus"] },
			{ label: t("shift_pickup_report.raw.column.confirmation_status"), sortColumns: ["confirmationStatus"] },
			{ label: t("shift_pickup_report.raw.column.confirmation_time"), sortColumns: ["confirmationTime"] },
		],
		[t],
	);

	const rawDefaultSortState = useMemo<SortingState<RawGridRow>[]>(
		() => [{ sortColumns: ["interactionDate"], sortOrder: "desc", label: t("shift_pickup_report.raw.column.interaction_date") }],
		[t],
	);

	const handleRowClick = useCallback((row: GroupedGridRow) => {
		setSelectedRequest({
			requestId: row.requestId,
			shift_start: row.shift_start,
			shift_end: row.shift_end,
		});
		setIsModalOpen(true);
	}, []);

	const handleCloseModal = useCallback(() => {
		setIsModalOpen(false);
		setSelectedRequest(null);
	}, []);

	return (
		<Box sx={{ p: 3, height: "100%", width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<Typography variant="h4" sx={{ color: "primary.dark" }}>
					{t("shift_pickup_report.title")}
				</Typography>
			</Box>
			<Box
				sx={{
					display: "flex",
					gap: 4,
					alignItems: "center",
					justifyContent: "space-between",
					border: "1px solid #E0E2E8",
					borderRadius: "8px",
					padding: "12px 16px",
				}}
			>
				<DateRangeSelector
					padding={0}
					removeBackground={true}
					removeBorder={true}
					startDate={startDate}
					endDate={endDate}
					onStartDateChange={setStartDate}
					onEndDateChange={setEndDate}
				/>
				<SystemFlatSwitch<AgentReportView>
					options={viewModeOptions}
					activeIndexOrValue={viewMode}
					onSwitch={(option) => setViewMode(option.value)}
					data-testid="shift-pickup-view-switch"
				/>
			</Box>
			{isRawView ? (
				<Box sx={{ flex: 1, minHeight: 0, width: "100%", display: "flex" }}>
					<SystemDataGrid<RawGridRow>
						rows={rawRows}
						columns={rawColumns}
						getRowId={(row) => row.id}
						loading={isRawLoading}
						error={isRawError}
						sortingOptions={rawSortingOptions}
						defaultSortState={rawDefaultSortState}
						localStorageKey={shiftPickupReportRawSortingKey}
						searchableColumns={["shiftDate", "staffName", "interactionType", "interactionStatus", "confirmationStatus"]}
						searchPlaceholder={t("shift_pickup_report.search_placeholder")}
						exportOptions={{ fileName: `short-term-call-in_${startDate.toFormat("yyyy-MM-dd")}_${endDate.toFormat("yyyy-MM-dd")}` }}
						emptyState={<EmptyState icon={<TableRowsIcon />} title={t("shift_pickup_report.empty_raw")} />}
						dataTestId="shift-pickup-raw"
					/>
				</Box>
			) : (
				<Box sx={{ flex: 1, minHeight: 0, width: "100%", display: "flex" }}>
					<SystemDataGrid<GroupedGridRow>
						rows={rowData}
						columns={groupedColumns}
						getRowId={(row) => row.requestId}
						loading={isLoading}
						sortingOptions={groupedSortingOptions}
						defaultSortState={groupedDefaultSortState}
						localStorageKey={shiftPickupReportGroupedSortingKey}
						emptyState={<EmptyState icon={<TableRowsIcon />} title={t("shift_pickup_report.empty_grouped")} />}
						dataGridProps={{ onRowClick: ({ row }) => handleRowClick(row) }}
						sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
						dataTestId="shift-pickup-grouped"
						exportOptions={{ fileName: `short-term-call-in_grouped_${startDate.toFormat("yyyy-MM-dd")}_${endDate.toFormat("yyyy-MM-dd")}` }}
					/>
					{selectedRequest && (
						<FillShiftModal
							open={isModalOpen}
							onClose={handleCloseModal}
							requestId={selectedRequest.requestId}
							shift_start={selectedRequest.shift_start}
							shift_end={selectedRequest.shift_end}
							displayFullDate
						/>
					)}
				</Box>
			)}
		</Box>
	);
};

export default ShiftPickupReport;
